/* eslint-disable @typescript-eslint/no-explicit-any */
// WelcomeView uses dynamic message payloads from the chat worker.
import React from 'react';
import { useSearchParams } from 'react-router';

import ChatWelcome from '@/sections/insights/aiAssistant/ChatWelcome';
import type { AIAssistantMode } from '@/sections/insights/aiAssistant/types';
import { extractConversationId } from '@/sections/insights/aiAssistant/utils/conversationId';

interface WelcomeViewProps {
  mode: AIAssistantMode;
  canUseAgentMode: boolean;
  isPraetorianUser: boolean;
  agents: any;
  isInputDisabled: boolean;
  isAIDisabledButBypassed: boolean;
  onExampleClick: (query: string, sendImmediately?: boolean) => Promise<void>;
  onModeChange: (mode: AIAssistantMode) => void;
  setNewMessage: (msg: string) => void;
  newMessage: string;
  createConversationWorker: (
    msg: string,
    mode: 'query' | 'agent'
  ) => Promise<any>;
  onSetMode: (mode: AIAssistantMode) => void;
  onSetPendingMission: () => void;
  setFailedMessage: (msg: string | null) => void;
}

export function WelcomeView({
  mode,
  canUseAgentMode,
  isPraetorianUser,
  agents,
  isInputDisabled,
  isAIDisabledButBypassed,
  onExampleClick,
  onModeChange,
  setNewMessage,
  newMessage,
  createConversationWorker,
  onSetMode,
  onSetPendingMission,
  setFailedMessage,
}: WelcomeViewProps) {
  const [, setSearchParams] = useSearchParams();

  return (
    <div key="welcome" className="h-full">
      <ChatWelcome
        onExampleClick={onExampleClick}
        mode={mode}
        autoFocus={!isInputDisabled}
        canUseAgentMode={canUseAgentMode}
        showAuto={isPraetorianUser}
        onModeChange={onModeChange}
        agents={agents}
        disabled={isAIDisabledButBypassed}
        onLaunchHunt={mode === 'auto' ? onSetPendingMission : undefined}
        onSendMessage={async message => {
          if (isInputDisabled) return;
          if (canUseAgentMode) {
            if (message === '/agent') {
              onSetMode('agent');
              setNewMessage('');
              return;
            }
            if (message === '/query') {
              onSetMode('query');
              setNewMessage('');
              return;
            }
          }
          setNewMessage('');
          try {
            const conv = await createConversationWorker(
              message,
              (mode === 'auto' ? 'agent' : mode) as 'query' | 'agent'
            );
            const newId = extractConversationId(conv);
            if (newId)
              setSearchParams(prev => {
                const p = new URLSearchParams(prev);
                p.set('conversationId', newId);
                return p;
              });
          } catch (err) {
            console.error('Failed to send message:', message, err);
            setFailedMessage(message);
          }
        }}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
      />
    </div>
  );
}
